// Per-browser, per-user SME onboarding state (WP1b).
//
// SME evaluators share persona logins on the demo build, so onboarding progress
// (welcome dismissal, essentials/all nav view, completed tours) must live per
// browser + user id — never server-side, and never leaking between users in the
// same browser. This module owns that state: a small localStorage-backed store
// with a useSyncExternalStore hook, modeled on lib/operatorMode.js (safe
// try/catch storage access, a shared listener set, no-throw in non-browser
// envs). It adds cross-tab awareness via the window 'storage' event and a
// cached snapshot per user id so the hook satisfies the getSnapshot stability
// contract (returning a fresh object on every call triggers an infinite render
// loop / React's "getSnapshot should be cached" warning).
import { useCallback, useSyncExternalStore } from 'react';

// Versioned, per-user localStorage key prefix. Matches the dha_prime__ shell
// convention (see AppShell LS_KEYS). Exported for spec/test helpers.
export const ONBOARDING_STORAGE_PREFIX = 'dha_prime__onboarding_v1__';

const NAV_VIEWS = Object.freeze(['essentials', 'all']);

// In-tab subscribers (React store callbacks). Notified on every local write.
const listeners = new Set();

// Cached snapshot per user id: { raw, record }. `raw` is the exact string last
// read from storage; when a later read returns the same string we hand back the
// same `record` reference, which keeps useSyncExternalStore stable.
const snapshotCache = new Map();

// Stable default handed to the hook/read for a falsy user id (anonymous edge
// cases must never touch storage or mint a fresh reference each render).
const EMPTY_USER_SNAPSHOT = defaultRecord();

function defaultRecord() {
  return { welcomeDismissedAt: null, navView: 'essentials', toursCompleted: [] };
}

function storageKey(userId) {
  return `${ONBOARDING_STORAGE_PREFIX}${userId}`;
}

// Coerce an arbitrary parsed value into a complete, valid record. Defaults are
// applied field-by-field so every field is always present and well-typed.
function normalize(partial) {
  const base = defaultRecord();
  if (!partial || typeof partial !== 'object') return base;
  return {
    welcomeDismissedAt:
      typeof partial.welcomeDismissedAt === 'string' ? partial.welcomeDismissedAt : base.welcomeDismissedAt,
    navView: NAV_VIEWS.includes(partial.navView) ? partial.navView : base.navView,
    toursCompleted: Array.isArray(partial.toursCompleted)
      ? partial.toursCompleted.filter((id) => typeof id === 'string')
      : base.toursCompleted,
  };
}

function readRaw(userId) {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
}

// Snapshot accessor with reference caching. Returns the same record object while
// the underlying storage string is unchanged.
function getSnapshot(userId) {
  if (!userId) return EMPTY_USER_SNAPSHOT;
  const raw = readRaw(userId);
  const cached = snapshotCache.get(userId);
  if (cached && cached.raw === raw) return cached.record;

  let parsed = null;
  if (raw != null) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null; // corrupt JSON -> default record, never throws
    }
  }
  const record = normalize(parsed);
  snapshotCache.set(userId, { raw, record });
  return record;
}

function notify() {
  listeners.forEach((listener) => listener());
}

function writeRecord(userId, record) {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(storageKey(userId), JSON.stringify(record));
    } catch {
      /* localStorage unavailable (private mode / test env) — no-throw */
    }
  }
  // Invalidate the cache so the next getSnapshot rebuilds a fresh reference,
  // which is what drives subscribed hooks to re-render.
  snapshotCache.delete(userId);
  notify();
}

/**
 * Read the current onboarding record for a user (defaults merged over any
 * stored partial). Never throws; a falsy user id yields the default record.
 */
export function readOnboardingState(userId) {
  return getSnapshot(userId);
}

/**
 * Merge `patch` over the current record, persist it, and notify subscribers.
 * Returns the merged record. A falsy user id is a no-op that returns defaults.
 */
export function updateOnboardingState(userId, patch) {
  if (!userId) return defaultRecord();
  const current = getSnapshot(userId);
  const next = normalize({ ...current, ...patch });
  writeRecord(userId, next);
  return next;
}

/** Stamp welcomeDismissedAt with the current time (ISO 8601). */
export function dismissWelcome(userId) {
  return updateOnboardingState(userId, { welcomeDismissedAt: new Date().toISOString() });
}

/** Set the nav view. Invalid values are ignored (current state is preserved). */
export function setNavView(userId, view) {
  if (!NAV_VIEWS.includes(view)) return readOnboardingState(userId);
  return updateOnboardingState(userId, { navView: view });
}

/** Add a tour id to toursCompleted, idempotently (no duplicates). */
export function markTourCompleted(userId, tourId) {
  if (!userId) return defaultRecord();
  const current = getSnapshot(userId);
  if (!tourId || current.toursCompleted.includes(tourId)) return current;
  return updateOnboardingState(userId, { toursCompleted: [...current.toursCompleted, tourId] });
}

/**
 * Resolve the post-login landing route. A user who has not yet dismissed the
 * welcome experience is routed to '/home'; otherwise the caller's default
 * landing is returned unchanged. A falsy user id always returns the default
 * landing as-is, so anonymous/edge login paths are never broken.
 */
export function resolveLanding(defaultLanding, userId) {
  if (!userId) return defaultLanding;
  const { welcomeDismissedAt } = readOnboardingState(userId);
  return welcomeDismissedAt == null ? '/home' : defaultLanding;
}

// Subscribe a React store callback to both in-tab writes and cross-tab storage
// events. Modeled on operatorMode's listener set, extended with the 'storage'
// event so onboarding progress made in another tab is reflected live.
function subscribe(callback) {
  listeners.add(callback);
  let onStorage;
  if (typeof window !== 'undefined') {
    onStorage = (event) => {
      // React to onboarding keys (or the storage.clear() case where key is null).
      if (!event || event.key == null || String(event.key).startsWith(ONBOARDING_STORAGE_PREFIX)) {
        callback();
      }
    };
    window.addEventListener('storage', onStorage);
  }
  return () => {
    listeners.delete(callback);
    if (onStorage) window.removeEventListener('storage', onStorage);
  };
}

/**
 * React hook returning the live onboarding record for a user id. Re-renders on
 * any in-tab update and on cross-tab storage events. Returns a stable reference
 * while the underlying state is unchanged.
 */
export function useOnboardingState(userId) {
  const getSnap = useCallback(() => getSnapshot(userId), [userId]);
  return useSyncExternalStore(subscribe, getSnap, getSnap);
}
